// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import { safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';

import './CodeEditor.less';

/**
 * `CodeEditor` component.
 */
class CodeEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            content: this.props.children,
            lineSeparator: this.getLineSeparator(this.props.children),
            options: {
                lineNumbers: true,
                lineWrapping: true,
                matchBrackets: true,
                matchTags: { bothTags: true },
                indentUnit: 4,
                indentWithTabs: true,
                enterMode: 'keep',
                tabMode: 'shift',
                ...props.options,
                extraKeys: { 'Ctrl-S': 'save' },
                autofocus: props.autofocus,
                readOnly: props.readOnly,
            },
        };
    }

    componentDidMount() {
        import(/* webpackChunkName: "codemirror" */ './CodeMirror')
            .then(({ CodeMirror }) => {
                this.CodeMirror = CodeMirror;
                const { onInit } = this.props;
                const { options } = this.state;

                this.codeMirror = CodeMirror.fromTextArea(this.textareaNode, options);
                this.updateMode(this.props);
                CodeMirror.commands.save = this.props.onSave;
                CodeMirror.commands.find = CodeMirror.commands.findPersistent;
                CodeMirror.commands.findNext = CodeMirror.commands.findPersistentNext;
                CodeMirror.commands.findPrev = CodeMirror.commands.findPersistentPrev;
                safeInvoke(onInit, this.codeMirror.display.wrapper);
                this.codeMirror.on('change', this.handleChange);
            });
    }

    componentWillReceiveProps({ mode, fileName, children, autofocus }) {
        this.updateMode({ mode, fileName });
        const newContent = children || '';

        if (newContent !== this.state.content) {
            const newState = {
                content: newContent,
                lineSeparator: this.getLineSeparator(newContent),
            };

            const prevScrollPosition = this.codeMirror.getScrollInfo();
            this.codeMirror.setValue(newContent);
            this.codeMirror.scrollTo(prevScrollPosition.left, prevScrollPosition.top);

            if (autofocus) {
                this.codeMirror.focus();
            }

            this.setState(newState);
        }
    }

    componentWillUnmount() {
        if (this.codeMirror) {
            this.codeMirror.toTextArea();
        }
    }

    findModeByFileName(fileName) {
        const modeInfo = this.CodeMirror.findModeByFileName(fileName);

        if (modeInfo) {
            return modeInfo.mode;
        }

        return null;
    }

    updateMode({ mode, fileName }) {
        if (!mode && fileName) {
            mode = this.findModeByFileName(fileName);
        }

        if (!mode) {
            return;
        }

        this.CodeMirror.loadMode(this.codeMirror, mode);
    }

    getLineSeparator(content = '') {
        const sep = /^.*?(\r\n?|\n)/.exec(content);

        return sep && sep[1] ? sep[1] : '\n';
    }

    handleChange = (doc, changes) => {
        if (this.props.readOnly || changes.origin === 'setValue') {
            return;
        }

        const { lineSeparator } = this.state;
        const { onChange } = this.props;
        const content = doc.getValue(lineSeparator);
        this.setState({ content }, () => {
            safeInvoke(onChange, content);
        });
    };

    render() {
        const {
            baseClassName,
            className,
            mode,
            fileName,
            readOnly,
            autofocus,
            children,
            onInit,
            onChange,
            onSave,
            options,
            ...props
        } = this.props;

        const { content } = this.state;

        return (
            <textarea
                style={{ display: 'none' }}
                ref={ref => (this.textareaNode = ref)}
                className={classNames(baseClassName, className)}
                defaultValue={content}
                {...props}
            />
        );
    }
}
CodeEditor.propTypes = {
    /**
     * The mode to use. When not given, this will default to the first mode that was loaded.
     */
    mode: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    /**
     * CodeEditor can detect mode by file name. Will be ignored if you set mode property.
     */
    fileName: PropTypes.string,
    /**
     * Content of the `CodeEditor`.
     */
    children: PropTypes.string,
    /**
     * Set read only.
     */
    readOnly: PropTypes.bool,
    /**
     * Set auto focus.
     */
    autofocus: PropTypes.bool,
    /**
     * onInit event handler.
     */
    onInit: PropTypes.func,
    /**
     * onChange event handler.
     */
    onChange: PropTypes.func,
    /**
     * onSave event handler.
     */
    onSave: PropTypes.func,
    /**
     * See all supported options in [CodeMirror](https://codemirror.net/doc/manual.html#config) documentation.
     */
    options: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
CodeEditor.defaultProps = {
    mode: '',
    fileName: '',
    children: '',
    readOnly: false,
    autofocus: false,
    onInit: null,
    onChange: null,
    onSave: null,
    options: null,
    className: null,
    baseClassName: `${CLS_PREFIX}code-editor`,
};

export default CodeEditor;



// WEBPACK FOOTER //
// ./components/CodeEditor/CodeEditor.js